// src/components/profile/Documents/ProtocolsCard.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import * as Lucide from "lucide-react";
import {
  Upload,
  FileText,
  Trash2,
  Edit2,
  Save,
  X,
  ArrowLeft,
  Plus,
  ChevronUp,
  ChevronDown,
  Image as ImageIcon,
  Wand2,
  Loader2,
  Eye,
  EyeOff,
  Info,
} from "lucide-react";
import Swal from "sweetalert2";
import {
  listProtocolDocs,
  uploadProtocolDoc,
  deleteProtocolDoc,
  updateProtocolDoc,
  getProtocolsConfig,
  updateProtocolsConfig,
  getProtocolsContent,
  updateProtocolsContent,
  downloadProtocolDocUrl,
} from "../../../services/protocolsService";
import { useNavigate } from "react-router-dom";
import { Banner } from "../../common/Banner";
import TooltipHelper from "../../common/TooltipHelper";
import SectionTips from "../../common/SectionTips";

/* ---------- estilos / util (alineado con Ethics / Prevention / IR) ---------- */
const input =
  "w-full rounded-2xl border border-gray-200 px-3 py-2 focus:ring-2 focus:ring-[#0488AF] outline-none bg-white";
const textareaBase =
  "w-full rounded-2xl border border-gray-200 px-3 py-2 focus:ring-2 focus:ring-[#0488AF] outline-none bg-white";
const btn =
  "inline-flex items-center gap-2 px-4 py-2 rounded-xl whitespace-nowrap";
const btnPri = `${btn} bg-[#0488AF] text-white hover:bg-[#037a98] disabled:opacity-60 disabled:cursor-not-allowed`;
const btnGhost = `${btn} border bg-white hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed`;
const btnWarn = `${btn} border bg-white hover:bg-amber-50 text-amber-800 border-amber-300 disabled:opacity-60 disabled:cursor-not-allowed`;
const btnIcon =
  "px-3 py-1.5 rounded-lg border hover:bg-gray-50 disabled:opacity-40";
const btnDanger =
  "inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-red-600 hover:bg-red-50";

// Botón azul “añadir …”
const btnAddBlue =
  "inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#0B4A8F] text-white hover:bg-[#093a70] shadow-sm";

const MAX_MB = 15;
const clone = (x) => JSON.parse(JSON.stringify(x || null));
const fmtBytes = (n) =>
  typeof n === "number" ? `${(n / 1024 / 1024).toFixed(2)} MB` : "";

/* ---------- helpers swal ---------- */
const showLoading = (title = "Procesando…") =>
  Swal.fire({
    title,
    html: "Por favor espera un momento.",
    allowOutsideClick: false,
    showConfirmButton: false,
    didOpen: () => Swal.showLoading(),
  });

const askConfirm = (opts = {}) =>
  Swal.fire({
    icon: "question",
    showCancelButton: true,
    confirmButtonText: opts.confirmButtonText || "Sí, continuar",
    cancelButtonText: "Cancelar",
    allowOutsideClick: false,
    ...opts,
  });

const toast = (title = "Listo", icon = "success", ms = 900) =>
  Swal.fire({ icon, title, timer: ms, showConfirmButton: false });

/* ---------- decoradores UI ---------- */
const Divider = () => (
  <div className="my-4">
    <div className="h-0.5 bg-gradient-to-r from-transparent via-gray-300 to-transparent rounded-full" />
  </div>
);


const Switch = ({
  checked,
  onChange,
  titleOn = "Visible para socios",
  titleOff = "Oculto para socios",
}) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 items-center rounded-full transition ${
      checked ? "bg-[#0488AF]" : "bg-gray-300"
    }`}
    title={checked ? titleOn : titleOff}
  >
    <span
      className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
        checked ? "translate-x-6" : "translate-x-1"
      }`}
    />
  </button>
);

/* ---------- validaciones CTA ---------- */
const isHttpOrRoute = (s) => {
  if (!s) return true;
  if (s.startsWith("/")) return true;
  try {
    const u = new URL(s);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
};
const isValidCtaUrl = (s) => {
  if (!s) return true;
  if (s.startsWith("mailto:")) return s.slice(7).includes("@");
  return isHttpOrRoute(s);
};

/* ---------- iconos ---------- */
const CURATED_ICONS = [
  "AlertTriangle",
  "ShieldCheck",
  "FileText",
  "ClipboardList",
  "GanttChart",
  "Users",
  "Megaphone",
  "Target",
  "Eye",
  "Lock",
];
const IconPreview = ({ name, size = 22, className }) => {
  const Cmp = (name && Lucide[name]) || FileText;
  return <Cmp size={size} className={className || "text-[#0B4A8F]"} />;
};

function IconPicker({ open, value, onSelect, onClose }) {
  const [q, setQ] = useState("");
  const pool = CURATED_ICONS.map((n) => ({
    name: n,
    Cmp: Lucide[n] || FileText,
  }));
  const icons = !q.trim()
    ? pool
    : pool.filter((it) => it.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[80] flex items-center justify-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/40" />
          <motion.div
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 30, opacity: 0 }}
            className="relative w-[min(900px,92vw)] max-h-[84vh] overflow-hidden rounded-2xl bg-white shadow-2xl"
          >
            <div className="p-4 border-b bg-gradient-to-r from-[#E6F0FF] to-[#E6FBFF] flex items-center justify-between">
              <div className="font-semibold">Elegir ícono</div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-white/70"
              >
                <X />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="flex items-center gap-2">
                <input
                  autoFocus
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Buscar…"
                  className={`${input} max-w-sm`}
                />
                {value && (
                  <div className="ml-auto flex items-center gap-2 text-sm text-gray-600">
                    Actual: <IconPreview name={value} />
                    <code className="bg-gray-100 rounded px-2 py-1 text-xs">
                      {value}
                    </code>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 overflow-y-auto max-h-[58vh] pr-1">
                {icons.map(({ name, Cmp }) => (
                  <button
                    key={name}
                    onClick={() => {
                      onSelect?.(name);
                      onClose?.();
                    }}
                    className={`group border rounded-xl p-3 text-left hover:shadow-sm transition ${
                      value === name ? "border-[#0488AF]" : "border-gray-200"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Cmp size={24} className="text-[#0B4A8F]" />
                      <div className="font-medium">{name}</div>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">Lucide</div>
                  </button>
                ))}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ---------- editor de líneas (mismo diseño que Reglamentos Internos) ---------- */
function LinesEditor({
  label,
  value = [],
  onChange,
  placeholder = "Texto…",
  placeholderPrefix = null,
  addLabel = "Añadir",
  hint = null,
}) {
  const arr = Array.isArray(value) && value.length ? value : [""];

  const addAt = (idx) => {
    const next = [...arr];
    next.splice(idx, 0, "");
    onChange(next);
  };
  const removeAt = (idx) => {
    const next = [...arr];
    next.splice(idx, 1);
    onChange(next.length ? next : [""]);
  };
  const updateAt = (idx, text) => {
    const next = [...arr];
    next[idx] = text;
    onChange(next);
  };

  const getPlaceholder = (index) => {
    if (placeholderPrefix) {
      return `${placeholderPrefix} ${index + 1}`;
    }
    return placeholder;
  };

  return (
    <div className="space-y-2">
      {label && (
        <div className="block text-sm font-medium text-[#0B4A8F] mb-1 flex items-center gap-1 relative pt-2">
          {label}
          {hint && <TooltipHelper text={hint} />}
        </div>
      )}
      {arr.map((line, i) => (
        <div key={i} className="flex items-start gap-2 min-w-0">
          <textarea
            className={`${textareaBase} min-h-[56px]`}
            value={line}
            onChange={(e) => updateAt(i, e.target.value)}
            placeholder={getPlaceholder(i)}
          />
          <div className="flex flex-col gap-1 shrink-0">
            <button
              type="button"
              title="Añadir debajo"
              className="px-2 py-1.5 rounded-lg border hover:bg-gray-50"
              onClick={() => addAt(i + 1)}
            >
              <Plus size={16} />
            </button>
            <button
              type="button"
              title="Eliminar"
              className={`${btnIcon} text-red-600 hover:bg-red-50`}
              onClick={() => removeAt(i)}
              disabled={arr.length <= 1 && !arr[0]}
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={() => addAt(arr.length)}
        className={btnAddBlue}
      >
        <Plus size={16} /> {addLabel}
      </button>
    </div>
  );
}

/* ======================================================= */
export default function ProtocolsCard({ onBack }) {
  const navigate = useNavigate();
  const goBack = () => {
    if (typeof onBack === "function") return onBack();
    if (window.history.length > 1) return navigate(-1);
    navigate("/profile");
  };

  /* ===== Docs ===== */
  const [loading, setLoading] = useState(false);
  const [list, setList] = useState([]);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [versionTag, setVersionTag] = useState("");
  const [visibleNew, setVisibleNew] = useState(true);
  const fileRef = useRef(null);

  // feedback visual “Subiendo…”
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const progressTimerRef = useRef(null);
  const startFakeProgress = () => {
    stopFakeProgress();
    setUploadPct(10);
    progressTimerRef.current = setInterval(() => {
      setUploadPct((p) =>
        p >= 95 ? 95 : p + Math.max(1, Math.round((100 - p) * 0.05))
      );
    }, 180);
  };
  const stopFakeProgress = () => {
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  };

  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editVersionTag, setEditVersionTag] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  /* ===== Config (admin) ===== */
  const [cfgLoading, setCfgLoading] = useState(false);
  const [cfgSaving, setCfgSaving] = useState(false);
  const [cfg, setCfg] = useState({
    pageTitle: "",
    subtitle: "",
    lastReview: "",
  });
  const [cfgInitial, setCfgInitial] = useState(cfg);
  const hasCfgChanges = useMemo(
    () => JSON.stringify(cfg) !== JSON.stringify(cfgInitial),
    [cfg, cfgInitial]
  );

  /* ===== Content (público) ===== */
  const [cntLoading, setCntLoading] = useState(false);
  const [cntSaving, setCntSaving] = useState(false);
  const [cnt, setCnt] = useState({
    lastReview: "",
    protocols: [
      {
        title: "Evacuación",
        icon: "AlertTriangle",
        summary: [
          "Describe el procedimiento para evacuar la dependencia ante emergencias.",
        ],
        steps: ["Activar alarma", "Reunirse en punto seguro", "Contabilizar personal"],
        cta: {
          contactUrl: "mailto:seguridad@empresa.cl",
          policyUrl: "/politicas/emergencias",
        },
      },
    ],
  });
  const [cntInit, setCntInit] = useState(cnt);
  const hasCntChanges = useMemo(
    () => JSON.stringify(cnt) !== JSON.stringify(cntInit),
    [cnt, cntInit]
  );

  const [pickerOpenFor, setPickerOpenFor] = useState(null);

  // para colapsar/expandir cada protocolo (ojito)
  const [collapsed, setCollapsed] = useState(new Set());
  const toggleCollapsed = (idx) => {
    setCollapsed((prev) => {
      const next = new Set([...prev]);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  /* ===== cargar todo ===== */
  async function loadAll() {
    try {
      setLoading(true);
      setCfgLoading(true);
      setCntLoading(true);

      const [docs, conf, content] = await Promise.all([
        listProtocolDocs(),
        getProtocolsConfig().catch(() => null),
        getProtocolsContent().catch(() => null),
      ]);

      setList(Array.isArray(docs) ? docs : []);

      // Si el backend devuelve el valor por defecto, tratarlo como vacío para mostrar placeholder
      const pageTitleFromBackend = conf?.pageTitle != null ? conf.pageTitle : "";
      const subtitleFromBackend = conf?.subtitle != null ? conf.subtitle : "";
      const cfgNext = {
        pageTitle: pageTitleFromBackend === "Protocolos" ? "" : pageTitleFromBackend,
        subtitle: subtitleFromBackend,
        lastReview: String(conf?.lastReview ?? content?.lastReview ?? ""),
      };
      setCfg(cfgNext);
      setCfgInitial(cfgNext);

      if (content && typeof content === "object") {
        // Limpiar valores por defecto y strings vacíos
        const cleanProtocols = Array.isArray(content.protocols) && content.protocols.length
          ? content.protocols.map((proto) => {
              const cleaned = { ...proto };
              // Limpiar summary: filtrar strings vacíos y valores por defecto como "Contenido..."
              if (Array.isArray(cleaned.summary)) {
                cleaned.summary = cleaned.summary
                  .map((p) => String(p).trim())
                  .filter((p) => p && !p.toLowerCase().includes("contenido"));
              }
              // Limpiar steps: filtrar strings vacíos y valores por defecto
              if (Array.isArray(cleaned.steps)) {
                cleaned.steps = cleaned.steps
                  .map((s) => String(s).trim())
                  .filter((s) => s && !s.toLowerCase().includes("contenido"));
              }
              // Normalizar objeto cta: asegurar que los campos existan incluso si están vacíos
              if (cleaned.cta && typeof cleaned.cta === "object") {
                cleaned.cta = {
                  contactUrl: String(cleaned.cta.contactUrl || "").trim(),
                  policyUrl: String(cleaned.cta.policyUrl || "").trim(),
                  ...(cleaned.cta.reportUrl ? { reportUrl: String(cleaned.cta.reportUrl).trim() } : {}),
                  ...(cleaned.cta.url ? { url: String(cleaned.cta.url).trim() } : {}),
                  ...(cleaned.cta.label ? { label: String(cleaned.cta.label).trim() } : {}),
                };
                // Si ambos campos principales están vacíos, eliminar el objeto cta
                if (!cleaned.cta.contactUrl && !cleaned.cta.policyUrl && !cleaned.cta.reportUrl && !cleaned.cta.url) {
                  delete cleaned.cta;
                }
              }
              return cleaned;
            })
          : cnt.protocols;
        
        const safe = {
          lastReview: String(content.lastReview ?? cfgNext.lastReview ?? ""),
          protocols: cleanProtocols,
        };
        setCnt(safe);
        setCntInit(clone(safe));
        // Inicializar todos los protocolos como contraídos
        const protocolsCount = (safe.protocols || []).length;
        setCollapsed(new Set(Array.from({ length: protocolsCount }, (_, i) => i)));
      } else {
        const safe = { ...cnt, lastReview: cfgNext.lastReview || "" };
        setCnt(safe);
        setCntInit(clone(safe));
        // Inicializar todos los protocolos como contraídos
        const protocolsCount = (safe.protocols || []).length;
        setCollapsed(new Set(Array.from({ length: protocolsCount }, (_, i) => i)));
      }
    } catch (e) {
      console.error(e);
      Swal.fire(
        "Error",
        e?.message || "No se pudieron cargar los datos",
        "error"
      );
    } finally {
      setLoading(false);
      setCfgLoading(false);
      setCntLoading(false);
    }
  }
  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ===== Upload ===== */
  const pickFile = (f) => {
    if (!f) return;
    const isPdf = f.type === "application/pdf" || /\.pdf$/i.test(f.name || "");
    if (!isPdf) return Swal.fire("Archivo inválido", "Solo PDF.", "warning");
    if (f.size > MAX_MB * 1024 * 1024) {
      // Si es PDF, mostrar mensaje con link de compresión
      if (isPdf) {
        return Swal.fire({
          icon: "warning",
          title: "Archivo muy grande",
          html: `El archivo "${f.name}" es demasiado grande (máx ${MAX_MB} MB).<br/><br/>Por favor, comprime el archivo PDF antes de subirlo.<br/><br/><a href="https://www.ilovepdf.com/es/comprimir_pdf" target="_blank" rel="noopener noreferrer" style="color: #0488AF; text-decoration: underline;">Comprimir PDF aquí</a>`,
          confirmButtonText: "Entendido",
        });
      }
      return Swal.fire(
        "Archivo muy grande",
        `Máximo ${MAX_MB} MB (tiene ${fmtBytes(f.size)})`,
        "warning"
      );
    }
    setFile(f);
    if (!title.trim()) setTitle((f.name || "").replace(/\.pdf$/i, ""));
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return Swal.fire("Atención", "Selecciona un PDF", "info");
    try {
      setUploading(true);
      startFakeProgress();
      await uploadProtocolDoc({
        file,
        title: title.trim() || undefined,
        versionTag: (versionTag || "").trim() || undefined,
        isPublished: !!visibleNew,
      });
      setUploadPct(100);
      await toast("Documento subido");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      setTitle("");
      setVersionTag("");
      setVisibleNew(true);
      await loadAll();
    } catch (e) {
      Swal.fire("Error", e?.message || "No se pudo subir", "error");
    } finally {
      stopFakeProgress();
      setUploading(false);
      setTimeout(() => setUploadPct(0), 400);
    }
  };

  /* ===== Edit doc ===== */
  const beginEdit = (r) => {
    setEditingId(r.id);
    setEditTitle(r.title || "");
    setEditVersionTag(r.versionTag || "");
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditTitle("");
    setEditVersionTag("");
    setSavingEdit(false);
  };
  const saveEdit = async (id) => {
    try {
      setSavingEdit(true);
      showLoading("Guardando…");
      await updateProtocolDoc(id, {
        title: (editTitle || "").trim() || undefined,
        versionTag: (editVersionTag || "").trim() || undefined,
      });
      Swal.close();
      await toast("Actualizado");
      cancelEdit();
      await loadAll();
    } catch (e) {
      Swal.close();
      Swal.fire("Error", e?.message || "No se pudo actualizar", "error");
    } finally {
      setSavingEdit(false);
    }
  };
  const togglePublish = async (id, v) => {
    try {
      await updateProtocolDoc(id, { isPublished: !!v });
      await loadAll();
    } catch (e) {
      Swal.fire(
        "Error",
        e?.message || "No se pudo cambiar visibilidad",
        "error"
      );
    }
  };
  const removeDoc = async (id) => {
    const doc = list.find((r) => r.id === id);
    const ok = await askConfirm({
      title: "Eliminar documento",
      text: `¿Seguro que deseas eliminar "${
        doc?.title || "este documento"
      }"? Esta acción no se puede deshacer.`,
      confirmButtonText: "Sí, eliminar",
    });
    if (!ok.isConfirmed) return;
    try {
      showLoading("Eliminando…");
      await deleteProtocolDoc(id);
      Swal.close();
      await toast("Eliminado");
      await loadAll();
    } catch (e) {
      Swal.close();
      Swal.fire("Error", e?.message || "No se pudo eliminar", "error");
    }
  };

  /* ===== Config ===== */
  const saveConfig = async () => {
    try {
      setCfgSaving(true);
      showLoading("Guardando configuración…");

      const toSend = {
        pageTitle: (cfg?.pageTitle || "").trim(),
        subtitle: (cfg?.subtitle || "").trim(),
        lastReview: (cfg?.lastReview || "").trim(),
      };

      const savedCfg = await updateProtocolsConfig(toSend);

      // sincronizamos también el lastReview en el contenido
      const contentPayload = {
        lastReview: toSend.lastReview,
        protocols: Array.isArray(cnt.protocols) ? cnt.protocols : [],
      };
      const savedContent = await updateProtocolsContent(contentPayload);

      // Si el backend devuelve el valor por defecto, tratarlo como vacío para mostrar placeholder
      const savedPageTitle = savedCfg?.pageTitle != null ? savedCfg.pageTitle : "";
      const savedSubtitle = savedCfg?.subtitle != null ? savedCfg.subtitle : "";
      const persistedCfg = {
        pageTitle: savedPageTitle === "Protocolos" ? "" : savedPageTitle,
        subtitle: savedSubtitle,
        lastReview: savedCfg?.lastReview != null ? savedCfg.lastReview : toSend.lastReview,
      };
      setCfg(persistedCfg);
      setCfgInitial(persistedCfg);

      const cleanCnt = {
        lastReview: String(
          savedContent?.lastReview ?? toSend.lastReview ?? ""
        ),
        protocols: Array.isArray(savedContent?.protocols)
          ? savedContent.protocols
          : contentPayload.protocols,
      };
      setCnt(cleanCnt);
      setCntInit(cleanCnt);

      Swal.close();
      await toast("Configuración guardada");
    } catch (e) {
      Swal.close();
      Swal.fire("Error", e?.message || "No se pudo guardar", "error");
    } finally {
      setCfgSaving(false);
    }
  };
  const discardConfig = () => setCfg(cfgInitial);

  /* ===== Content actions ===== */
  const addProtocol = () =>
    setCnt((c) => ({
      ...c,
      protocols: [
        ...(c.protocols || []),
        {
          title: "",
          icon: "FileText",
          summary: [""],
          steps: [""],
        },
      ],
    }));
  const removeProtocol = (i) =>
    setCnt((c) => ({
      ...c,
      protocols: (c.protocols || []).filter((_, idx) => idx !== i),
    }));
  const moveProtocol = (i, dir) =>
    setCnt((c) => {
      const arr = [...(c.protocols || [])];
      const ni = i + dir;
      if (ni < 0 || ni >= arr.length) return c;
      const [it] = arr.splice(i, 1);
      arr.splice(ni, 0, it);
      return { ...c, protocols: arr };
    });
  const updateProtocolField = (i, field, value) =>
    setCnt((c) => {
      const arr = [...(c.protocols || [])];
      arr[i] = { ...(arr[i] || {}), [field]: value };
      return { ...c, protocols: arr };
    });

  /* ===== Save content ===== */
  const saveContent = async () => {
    try {
      setCntSaving(true);
      showLoading("Guardando contenido…");

      const payload = clone(cnt);
      const saved = await updateProtocolsContent(payload);

      const clean = {
        lastReview: String(saved?.lastReview ?? payload.lastReview ?? ""),
        protocols: Array.isArray(saved?.protocols)
          ? saved.protocols
          : payload.protocols,
      };

      setCnt(clean);
      setCntInit(clone(clean));

      // si el backend devuelve lastReview desde content y config no lo tiene, lo usamos
      if (clean.lastReview && !cfg.lastReview) {
        setCfg((c) => ({ ...c, lastReview: clean.lastReview }));
        setCfgInitial((c) => ({ ...c, lastReview: clean.lastReview }));
      }

      Swal.close();
      await toast("Contenido actualizado");
    } catch (e) {
      Swal.close();
      Swal.fire("Error", e?.message || "No se pudo guardar", "error");
    } finally {
      setCntSaving(false);
    }
  };
  const discardContent = () => setCnt(clone(cntInit));

  const ordered = useMemo(
    () =>
      [...list].sort((a, b) => {
        const ta = a?.createdAt ? +new Date(a.createdAt) : 0;
        const tb = b?.createdAt ? +new Date(b.createdAt) : 0;
        return tb - ta;
      }),
    [list]
  );

  /* ===== render ===== */
  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="p-6 max-w-7xl mx-auto space-y-6"
      >
        <button
          onClick={goBack}
          className="inline-flex items-center gap-3 px-3 py-2 bg-[#DC3465] hover:bg-[#c02e59] rounded-xl text-white"
        >
          <ArrowLeft size={18} /> Volver al menú principal
        </button>

        <Banner
          title="Protocolos"
          message="Sube, edita y controla visibilidad del documento. Ajusta encabezados y contenido público."
        />

        <SectionTips
          section="la sección de Protocolos"
          buttonTitle="¿Cómo usar esta sección?"
          tipsTitle="¿Cómo usar Protocolos?"
          tipsList={[
            'En esta sección puedes subir el documento PDF de protocolos y controlar su visibilidad para los socios.',
            'Puedes personalizar los textos visibles en la página pública, como el título, subtítulo y última revisión.',
            'Puedes crear principios que se mostrarán en la página pública como chips reordenables.',
            'Puedes crear secciones con contenido personalizado, cada una con párrafos, puntos y botones de acción.',
            'Cada sección puede tener un ícono o imagen personalizada.',
            'Los documentos subidos se pueden mostrar u ocultar para los socios mediante el switch de visibilidad.',
          ]}
          finalNote="Consejo: Organiza bien las secciones y principios para crear una experiencia clara para los socios."
          color="#0488AF"
          videoUrl="https://res.cloudinary.com/duz01skao/video/upload/v1766416278/Video_Tutorial_m%C3%B3dulo_Administraci%C3%B3n_-_Protocolos_ys2zsm.mp4"
          manualUrl="https://res.cloudinary.com/duz01skao/image/upload/v1766412946/Manual_de_usuario_administraci%C3%B3n_bsvsyl.pdf"
        />

        {/* SUBIR + CONFIG */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 min-w-0">
          {/* Subir */}
          <div
            className="rounded-2xl p-[1.5px] min-w-0 flex-shrink-0"
            style={{
              background: "linear-gradient(135deg, #E6F0FF 0%, #E6FBFF 100%)",
            }}
          >
            <div className="rounded-2xl bg-white ring-1 ring-black/5 overflow-visible">
              <div className="p-5">
                <h2 className="text-lg font-semibold mb-3">
                  Subir documento (PDF)
                </h2>

                <form
                  onSubmit={handleUpload}
                  className="grid gap-3 md:grid-cols-3 items-end"
                >
                  <div className="md:col-span-1 flex items-end gap-2">
                    <label className={`${btnGhost} cursor-pointer`}>
                      <Upload size={16} />
                      Seleccionar archivo
                      <input
                        ref={fileRef}
                        type="file"
                        accept="application/pdf"
                        className="hidden"
                        onChange={(e) => pickFile(e.target.files?.[0])}
                        disabled={uploading}
                      />
                    </label>
                  </div>

                  {file && (
                    <div className="md:col-span-3">
                      <div className="flex items-center gap-3 p-3 rounded-xl border bg-slate-50">
                        <div className="p-2 rounded-lg bg-blue-50 border border-blue-100">
                          <FileText className="w-5 h-5 text-[#0488AF]" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-medium truncate">
                            {file.name}
                          </div>
                        </div>
                        <button
                          type="button"
                          className={`${btnGhost} shrink-0`}
                          onClick={() => {
                            setFile(null);
                            if (fileRef.current) fileRef.current.value = "";
                          }}
                          disabled={uploading}
                          title="Quitar archivo"
                        >
                          <X size={16} /> Quitar
                        </button>
                      </div>
                    </div>
                  )}

                  <label className="md:col-span-3">
                    <span className="block text-sm text-gray-700 mb-1">
                      Título (opcional)
                    </span>
                    <input
                      className={input}
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Protocolo general"
                      disabled={uploading}
                    />
                  </label>

                  <label>
                    <span className="block text-sm text-gray-700 mb-1">
                      Etiqueta de versión (opcional)
                    </span>
                    <input
                      className={input}
                      value={versionTag}
                      onChange={(e) => setVersionTag(e.target.value)}
                      placeholder="v2025-10-26"
                      disabled={uploading}
                    />
                  </label>

                  <div className="md:col-span-3 flex flex-wrap items-center gap-3">

                    <button
                      type="submit"
                      disabled={!file || loading || uploading}
                      className={btnPri}
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="animate-spin" size={16} />{" "}
                          Subiendo… {uploadPct ? `${uploadPct}%` : ""}
                        </>
                      ) : (
                        <>Subir documento</>
                      )}
                    </button>

                    {uploading && (
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#0488AF] transition-all"
                          style={{ width: `${uploadPct || 25}%` }}
                        />
                      </div>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Config */}
          <div
            className="rounded-2xl p-[2px] min-w-0 flex-shrink-0"
            style={{
              background: "linear-gradient(135deg, #DCEBFF 0%, #DDF8FF 100%)",
            }}
          >
            <div className="rounded-2xl bg-white ring-1 ring-black/5 overflow-visible">
              <div className="p-5 space-y-6">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-lg font-semibold">
                    Textos visibles en la página pública
                  </h3>
                  <button
                    type="button"
                    onClick={discardConfig}
                    disabled={!hasCfgChanges}
                    className={btnWarn}
                  >
                    <X size={14} /> Descartar cambios
                  </button>
                </div>

                <div className="rounded-2xl border bg-[#F8FAFC] p-4 space-y-3">
                  <label className="block">
                    <span className="block text-sm text-gray-700 flex items-center gap-1">
                      Título
                      <TooltipHelper text='Si lo dejas vacío, se usará "Protocolos".' />
                    </span>
                    <input
                      className={input}
                      value={cfg.pageTitle || ""}
                      onChange={(e) =>
                        setCfg((s) => ({
                          ...s,
                          pageTitle: e.target.value,
                        }))
                      }
                      placeholder="Protocolos"
                      disabled={cfgLoading}
                    />
                  </label>
                  <label className="block">
                    <span className="block text-sm text-gray-700 flex items-center gap-1">
                      Subtítulo
                      <TooltipHelper text='Si lo dejas vacío, se usará "Guías y procedimientos oficiales".' />
                    </span>
                    <input
                      className={input}
                      value={cfg.subtitle || ""}
                      onChange={(e) =>
                        setCfg((s) => ({
                          ...s,
                          subtitle: e.target.value,
                        }))
                      }
                      placeholder="Guías y procedimientos oficiales"
                      disabled={cfgLoading}
                    />
                  </label>
                  <label className="block">
                    <span className="block text-sm text-gray-700">
                      Última revisión (opcional)
                    </span>
                    <input
                      className={input}
                      value={cfg.lastReview || ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        setCfg((s) => ({ ...s, lastReview: v }));
                        setCnt((c) => ({ ...c, lastReview: v }));
                      }}
                      placeholder="Octubre 2025"
                      disabled={cfgLoading}
                    />
                  </label>
                </div>

                <div className="pt-1">
                  <button
                    type="button"
                    onClick={saveConfig}
                    disabled={cfgSaving || !hasCfgChanges}
                    className={btnPri}
                  >
                    <Save size={16} /> Guardar configuración
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* separación */}
        <Divider />
        <Divider />

        {/* LISTA DOCS (alineado al resto) */}
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-gray-800">Documentos subidos</h3>
        </div>
        {loading ? (
          <div className="text-gray-500 py-6">Cargando…</div>
        ) : (
          <div className="space-y-3">
            {ordered.length === 0 ? (
              <div className="text-gray-500 py-6">Aún no hay documentos.</div>
            ) : (
              ordered.map((r) => {
                const url = r.secureUrl || downloadProtocolDocUrl(r.id);
                const isEditing = editingId === r.id;
                return (
                  <motion.div
                    key={r.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2 }}
                    className="border rounded-xl p-3 flex flex-wrap items-start justify-between gap-3 bg-white/70 min-w-0"
                  >
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className="p-2 rounded-lg bg-blue-50 border border-blue-100 shrink-0">
                        <FileText className="w-5 h-5 text-[#0488AF]" />
                      </div>

                      {isEditing ? (
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="px-2 py-1 rounded-lg bg-amber-100 text-amber-800 text-xs font-semibold">
                            Editando…
                          </span>
                          <input
                            value={editTitle}
                            onChange={(e) => setEditTitle(e.target.value)}
                            className={`${input} w-[16rem]`}
                            placeholder="Título"
                          />
                          <input
                            value={editVersionTag}
                            onChange={(e) => setEditVersionTag(e.target.value)}
                            className={`${input} w-[10rem]`}
                            placeholder="v2025-10-26"
                          />
                        </div>
                      ) : (
                        <div className="min-w-0">
                          {url ? (
                            <a
                              href={url}
                              target="_blank"
                              rel="noreferrer"
                              className="font-medium text-[#0488AF] hover:underline truncate break-words"
                              title={r.title?.trim() || "Protocolo"}
                            >
                              {r.title?.trim() || "Protocolo"}
                            </a>
                          ) : (
                            <span className="font-medium truncate break-words">
                              {r.title?.trim() || "Protocolo"}
                            </span>
                          )}
                          <div className="text-xs text-gray-600">
                            {`Versión: ${r.versionTag || "—"} · ${
                              r.createdAt
                                ? new Date(r.createdAt).toLocaleString()
                                : ""
                            }`}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-2 shrink-0 flex-shrink-0">
                      <div className="flex items-center gap-2 px-2 py-1 rounded border bg-white">
                        <Switch
                          checked={!!r.isPublished}
                          onChange={(v) => togglePublish(r.id, v)}
                        />
                        <span className="text-xs">
                          {r.isPublished
                            ? "Visible para socios"
                            : "Oculto para socios"}
                        </span>
                      </div>

                      {isEditing ? (
                        <>
                          <button
                            className={`${btnGhost} px-3 py-1.5 rounded-lg`}
                            disabled={savingEdit}
                            onClick={() => saveEdit(r.id)}
                            title="Guardar"
                          >
                            <Save size={16} />
                          </button>
                          <button
                            className={`${btnGhost} px-3 py-1.5 rounded-lg`}
                            onClick={cancelEdit}
                            title="Cancelar"
                          >
                            <X size={16} />
                          </button>
                        </>
                      ) : (
                        <button
                          className={`${btnGhost} px-3 py-1.5 rounded-lg`}
                          onClick={() => beginEdit(r)}
                          title="Editar"
                        >
                          <Edit2 size={16} />
                        </button>
                      )}

                      {url && (
                        <a
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border hover:bg-gray-50"
                          title="Ver / Descargar"
                        >
                          <Eye size={16} /> Ver
                        </a>
                      )}

                      <button
                        className={btnDanger}
                        onClick={() => removeDoc(r.id)}
                        title="Eliminar"
                      >
                        <Trash2 size={16} /> Eliminar
                      </button>
                    </div>
                  </motion.div>
                );
              })
            )}
          </div>
        )}

        {/* CONTENIDO PÚBLICO */}
        <div
          className="rounded-2xl p-[1.5px]"
          style={{
            background: "linear-gradient(135deg, #E6F0FF 0%, #E6FBFF 100%)",
          }}
        >
          <div className="rounded-2xl bg-white ring-1 ring-black/5 overflow-visible">
            <div className="p-5">
              <h3 className="text-lg font-semibold mb-4">
                Contenido visible en la página pública
              </h3>
              <div className="rounded-2xl border overflow-hidden">
                <div className="bg-gradient-to-r from-[#E6F0FF] to-[#E6FBFF] px-4 py-2 border-b flex items-center justify-between">
                  <div className="text-sm font-semibold text-gray-800">
                    Protocolos
                  </div>
                </div>

                <div className="p-4 space-y-6">
                  {(cnt.protocols || []).map((p, i) => {
                    const isCollapsed = collapsed.has(i);
                    return (
                      <div
                        key={i}
                        className="rounded-2xl border shadow-sm overflow-hidden"
                      >
                        <div className="h-1 w-full bg-gradient-to-r from-[#0488AF] to-[#C42251]" />
                        <div className="p-3 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 min-w-0 flex-1">
                              <span className="px-2 py-1 rounded-lg bg-blue-50 text-[#0B4A8F] text-xs font-semibold flex-shrink-0">
                                Protocolo {i + 1}
                              </span>
                              {(p.iconImageUrl || p.icon) && (
                                <span className="flex-shrink-0">
                                  {p.iconImageUrl ? (
                                    <img
                                      src={p.iconImageUrl}
                                      alt=""
                                      className="h-5 w-5 object-contain rounded"
                                    />
                                  ) : (
                                    <IconPreview name={p.icon} size={20} />
                                  )}
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={() => toggleCollapsed(i)}
                                className="text-xs text-gray-600 underline-offset-2 hover:underline truncate"
                                title={
                                  isCollapsed
                                    ? "Expandir protocolo"
                                    : "Contraer protocolo"
                                }
                              >
                                {p.title || "Editar título"}
                              </button>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <button
                                className={btnIcon}
                                onClick={() => toggleCollapsed(i)}
                                title={isCollapsed ? "Expandir" : "Contraer"}
                              >
                                {isCollapsed ? (
                                  <Eye size={16} />
                                ) : (
                                  <EyeOff size={16} />
                                )}
                              </button>
                              <button
                                className={btnIcon}
                                disabled={i === 0}
                                onClick={() => moveProtocol(i, -1)}
                                title="Mover arriba"
                              >
                                <ChevronUp size={16} />
                              </button>
                              <button
                                className={btnIcon}
                                disabled={
                                  i === (cnt.protocols?.length || 0) - 1
                                }
                                onClick={() => moveProtocol(i, +1)}
                                title="Mover abajo"
                              >
                                <ChevronDown size={16} />
                              </button>
                              <button
                                className={btnDanger}
                                onClick={() => removeProtocol(i)}
                                title="Eliminar protocolo"
                              >
                                <Trash2 size={16} />{" "}
                                <span className="hidden sm:inline">
                                  Eliminar
                                </span>
                              </button>
                            </div>
                          </div>
                        </div>

                        {!isCollapsed && (
                          <div className="p-4 space-y-3">
                            <label>
                              <span className="block text-sm text-gray-700 mb-1">
                                Título
                              </span>
                              <input
                                className={input}
                                value={p.title || ""}
                                onChange={(e) =>
                                  updateProtocolField(
                                    i,
                                    "title",
                                    e.target.value
                                  )
                                }
                                placeholder="Título nuevo protocolo"
                              />
                            </label>

                            <div className="space-y-2">
                              <span className="block text-sm text-gray-700">
                                Ícono (Lucide) o Imagen
                              </span>
                              <div className="flex flex-wrap items-center gap-2">
                                <div className="flex items-center gap-2 rounded-xl border px-3 py-1.5 bg-gray-50">
                                  <IconPreview name={p.icon} />
                                  <code className="text-xs">
                                    {p.icon || "—"}
                                  </code>
                                </div>
                                <button
                                  type="button"
                                  className={`${btnGhost} px-3 py-1.5 rounded-lg`}
                                  onClick={() => setPickerOpenFor(i)}
                                  title="Elegir ícono"
                                >
                                  <Wand2 size={16} /> Elegir ícono
                                </button>
                                <button
                                  type="button"
                                  className={btnIcon}
                                  onClick={() =>
                                    updateProtocolField(i, "icon", "")
                                  }
                                  title="Quitar ícono"
                                >
                                  <X size={16} />
                                </button>
                              </div>

                              <div className="flex flex-wrap items-center gap-2">
                                <ImageIcon
                                  size={16}
                                  className="text-gray-500"
                                />
                                <input
                                  className={input}
                                  value={p.iconImageUrl || ""}
                                  onChange={(e) =>
                                    updateProtocolField(
                                      i,
                                      "iconImageUrl",
                                      e.target.value
                                    )
                                  }
                                  placeholder="https://…/icon.png (opcional)"
                                />
                                {p.iconImageUrl && (
                                  <button
                                    type="button"
                                    className={btnIcon}
                                    onClick={() =>
                                      updateProtocolField(
                                        i,
                                        "iconImageUrl",
                                        ""
                                      )
                                    }
                                    title="Quitar imagen"
                                  >
                                    <X size={16} />
                                  </button>
                                )}
                              </div>
                              <div className="text-xs text-gray-500">
                                Si indicas una imagen, tendrá prioridad sobre
                                el ícono.
                              </div>
                            </div>

                            {/* Párrafos + pasos con mismo diseño de Reglamentos */}
                            <div className="grid gap-4 md:grid-cols-2 mt-8">
                              <LinesEditor
                                label="Párrafos"
                                value={Array.isArray(p.summary) ? p.summary : []}
                                onChange={(arr) =>
                                  setCnt((c) => {
                                    const arrProt = [...(c.protocols || [])];
                                    arrProt[i] = {
                                      ...(arrProt[i] || {}),
                                      summary: arr,
                                    };
                                    return { ...c, protocols: arrProt };
                                  })
                                }
                                placeholderPrefix="Párrafo"
                                addLabel="Añadir párrafo"
                                hint="Cada párrafo aparecerá como un bloque de texto separado en la página pública."
                              />
                              <LinesEditor
                                label="Pasos"
                                value={Array.isArray(p.steps) ? p.steps : []}
                                onChange={(arr) =>
                                  setCnt((c) => {
                                    const arrProt = [...(c.protocols || [])];
                                    arrProt[i] = {
                                      ...(arrProt[i] || {}),
                                      steps: arr,
                                    };
                                    return { ...c, protocols: arrProt };
                                  })
                                }
                                placeholderPrefix="Paso"
                                addLabel="Añadir paso"
                                hint="Cada paso aparecerá con el título 'Pasos' y se enumerará automáticamente en la página pública para los socios."
                              />
                            </div>

                            <div className="grid gap-3 md:grid-cols-2">
                              <label>
                                <span className="block text-sm text-gray-700 mb-1 flex items-center gap-1">
                                  Botón de acción: Contacto (URL o mailto)
                                  <TooltipHelper text="Para correos usa mailto:correo@ejemplo.com. Para páginas web usa http:// o https://" />
                                </span>
                                <input
                                  className={input}
                                  value={p.cta?.contactUrl || ""}
                                  onChange={(e) =>
                                    updateProtocolField(i, "cta", {
                                      contactUrl: e.target.value || "",
                                      policyUrl: p.cta?.policyUrl || "",
                                      ...(p.cta?.reportUrl ? { reportUrl: p.cta.reportUrl } : {}),
                                      ...(p.cta?.url ? { url: p.cta.url } : {}),
                                      ...(p.cta?.label ? { label: p.cta.label } : {}),
                                    })
                                  }
                                  placeholder="mailto:contacto@empresa.cl"
                                />
                                {p.cta?.contactUrl &&
                                  !isValidCtaUrl(p.cta.contactUrl) && (
                                    <div className="text-xs text-rose-700 mt-1">
                                      Debe comenzar con http(s)://, / o mailto:
                                    </div>
                                  )}
                              </label>
                              <label>
                                <span className="block text-sm text-gray-700 mb-1 flex items-center gap-1">
                                  Botón de acción: Política (URL o /ruta)
                                  <TooltipHelper text="Ruta interna (/) o URL externa (http:// o https://)" />
                                </span>
                                <input
                                  className={input}
                                  value={p.cta?.policyUrl || ""}
                                  onChange={(e) =>
                                    updateProtocolField(i, "cta", {
                                      contactUrl: p.cta?.contactUrl || "",
                                      policyUrl: e.target.value || "",
                                      ...(p.cta?.reportUrl ? { reportUrl: p.cta.reportUrl } : {}),
                                      ...(p.cta?.url ? { url: p.cta.url } : {}),
                                      ...(p.cta?.label ? { label: p.cta.label } : {}),
                                    })
                                  }
                                  placeholder="/politicas/protocolos"
                                />
                                {p.cta?.policyUrl &&
                                  !isHttpOrRoute(p.cta.policyUrl) && (
                                    <div className="text-xs text-rose-700 mt-1">
                                      Debe comenzar con http(s):// o /
                                    </div>
                                  )}
                              </label>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {(cnt.protocols || []).length === 0 && (
                    <div className="text-sm text-gray-500">
                      Sin protocolos — agrega al menos uno.
                    </div>
                  )}
                </div>

                {/* Footer: botón agregar */}
                <div className="px-4 py-3 bg-gray-50 border-t">
                  <button
                    type="button"
                    onClick={addProtocol}
                    className={btnAddBlue}
                  >
                    <Plus size={16} /> Agregar protocolo
                  </button>
                </div>
              </div>

              {/* Botón Guardar fuera de los protocolos */}
              <div className="px-5 pb-5 pt-4 border-t border-gray-200">
                <div className="flex flex-wrap gap-2 justify-end">
                  <button
                    type="button"
                    onClick={saveContent}
                    disabled={cntSaving || !hasCntChanges}
                    className={btnPri}
                  >
                    <Save size={16} /> Guardar contenido
                  </button>
                  <button
                    type="button"
                    onClick={discardContent}
                    disabled={cntSaving || !hasCntChanges}
                    className={btnGhost}
                  >
                    <X size={16} /> Descartar cambios
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Modal IconPicker */}
      <IconPicker
        open={pickerOpenFor != null}
        value={
          pickerOpenFor != null ? cnt.protocols?.[pickerOpenFor]?.icon : undefined
        }
        onSelect={(name) => {
          if (pickerOpenFor == null) return;
          setCnt((c) => {
            const arr = [...(c.protocols || [])];
            arr[pickerOpenFor] = { ...(arr[pickerOpenFor] || {}), icon: name };
            return { ...c, protocols: arr };
          });
        }}
        onClose={() => setPickerOpenFor(null)}
      />
    </>
  );
}
